<template>
  <v-card color="grey lighten-4" outlined class="px-3 py-1 pb-4 mt-4">
    <div class="d-flex justify-space-between align-center">
      <div>
        <span style="font-weight: bold">
          {{ cardIdentifier.card_identifier_type.title }}
        </span>
        <span class="ml-1" v-if="!readonly">
          <v-btn
            icon
            color="red darken-3"
            @click="handleDeleteCardIdentifier(cardIdentifier)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </span>
      </div>
      <div>
        <v-btn
          v-if="isShowAdvancedMode"
          small
          text
          color="primary"
          @click="showAdvanced = !showAdvanced"
        >
          {{ showAdvanced ? 'แสดงข้อมูลพื้นฐาน' : 'แสดงข้อมูลขั้นสูง' }}
        </v-btn>
      </div>
    </div>

    <div class="mt-1">
      <v-form ref="cardIdentifierForm" v-model="isValid">
        <v-text-field
          v-show="
            cardIdentifier.default_display_encoding.sn_base_10 || showAdvanced
          "
          v-model="cardIdentifier.sn.base_10"
          class="fix-google-chrome mb-2"
          :clearable="!readonly"
          outlined
          dense
          :rules="[
            atLeastOneRequired,
            (v) => !v || /^[0-9]+$/.test(v) || 'โปรดระบุเลขฐาน 10 ให้ถูกต้อง',
          ]"
          :readonly="readonly"
        >
          <template v-slot:append>
            <div class="d-flex align-center">
              <v-btn
                icon
                v-if="showAdvanced && !readonly"
                @click="handleDisplayEncoding('sn_base_10', cardIdentifier)"
                :disabled="
                  cardIdentifier.default_display_encoding.sn_base_16 ===
                    false &&
                  cardIdentifier.default_display_encoding.sn_string === false
                "
              >
                <v-icon
                  color="yellow darken-3"
                  v-if="cardIdentifier.default_display_encoding.sn_base_10"
                  >mdi-star</v-icon
                >
                <v-icon v-else>mdi-star-outline</v-icon>
              </v-btn>
              <v-chip small>เลขฐาน 10</v-chip>
            </div>
          </template>
        </v-text-field>
        <v-text-field
          v-show="
            cardIdentifier.default_display_encoding.sn_base_16 || showAdvanced
          "
          v-model="cardIdentifier.sn.base_16"
          class="fix-google-chrome mb-2"
          :clearable="!readonly"
          outlined
          dense
          :rules="[
            atLeastOneRequired,
            (v) =>
              !v || /^[0-9A-Fa-f]+$/.test(v) || 'โปรดระบุเลขฐาน 16 ให้ถูกต้อง',
          ]"
          :readonly="readonly"
          @input="toUpperCase"
        >
          <template v-slot:append>
            <div class="d-flex align-center">
              <v-btn
                icon
                v-if="showAdvanced && !readonly"
                @click="handleDisplayEncoding('sn_base_16', cardIdentifier)"
                :disabled="
                  cardIdentifier.default_display_encoding.sn_base_10 ===
                    false &&
                  cardIdentifier.default_display_encoding.sn_string === false
                "
              >
                <v-icon
                  color="yellow darken-3"
                  v-if="cardIdentifier.default_display_encoding.sn_base_16"
                  >mdi-star</v-icon
                >
                <v-icon v-else>mdi-star-outline</v-icon>
              </v-btn>
              <v-chip small>เลขฐาน 16</v-chip>
            </div>
          </template>
        </v-text-field>
        <v-text-field
          v-show="
            cardIdentifier.default_display_encoding.sn_string || showAdvanced
          "
          v-model="cardIdentifier.sn.string"
          class="fix-google-chrome"
          :clearable="!readonly"
          outlined
          dense
          :rules="[
            atLeastOneRequired,
            (v) =>
              !v || /^[a-zA-Z0-9]+$/.test(v) || 'โปรดระบุตัวอักษรให้ถูกต้อง',
          ]"
          :readonly="readonly"
        >
          <template v-slot:append>
            <div class="d-flex align-center">
              <v-btn
                icon
                v-if="showAdvanced && !readonly"
                @click="handleDisplayEncoding('sn_string', cardIdentifier)"
                :disabled="
                  cardIdentifier.default_display_encoding.sn_base_10 ===
                    false &&
                  cardIdentifier.default_display_encoding.sn_base_16 === false
                "
              >
                <v-icon
                  color="yellow darken-3"
                  v-if="cardIdentifier.default_display_encoding.sn_string"
                  >mdi-star</v-icon
                >
                <v-icon v-else>mdi-star-outline</v-icon>
              </v-btn>
              <v-chip small>ตัวอักษร</v-chip>
            </div>
          </template>
        </v-text-field>
      </v-form>
    </div>

    <div class="mt-4" v-if="showAdvanced">
      <div class="mb-3">
        <div style="color: #555; font-size: 12px; margin-left: 5px">
          เริ่มต้นใช้งาน
        </div>
        <date-picker
          style="width: 100%"
          v-model="cardIdentifier.issue_datetime"
          type="datetime"
          format="DD/MM/YYYY HH:mm"
          :clearable="false"
          :disabled="readonly"
        ></date-picker>
      </div>
      <div class="mb-3">
        <div style="color: #555; font-size: 12px; margin-left: 5px">
          หมดอายุ
        </div>
        <date-picker
          style="width: 100%"
          v-model="cardIdentifier.expire_datetime"
          type="datetime"
          format="DD/MM/YYYY HH:mm"
          :clearable="false"
          :disabled="readonly"
        ></date-picker>
      </div>
      <div class="mb-3">
        <div style="color: #555; font-size: 12px; margin-left: 5px">
          ระงับชั่วคราว
        </div>
        <date-picker
          style="width: 100%"
          v-model="cardIdentifier.freeze_datetime"
          type="datetime"
          format="DD/MM/YYYY HH:mm"
          :clearable="false"
          :disabled="readonly"
        ></date-picker>
      </div>
      <div>
        <div style="color: #555; font-size: 12px; margin-left: 5px">
          ยกเลิกถาวร
        </div>
        <date-picker
          style="width: 100%"
          v-model="cardIdentifier.discard_datetime"
          type="datetime"
          format="DD/MM/YYYY HH:mm"
          :clearable="false"
          :disabled="readonly"
        ></date-picker>
      </div>
    </div>
  </v-card>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  components: {
    DatePicker,
  },
  props: {
    index: {
      type: Number, // Pass the index or identifier from parent
      required: true,
    },
    cardIdentifier: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isShowAdvancedMode: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showAdvanced: this.readonly ? false : true,
      isValid: false,
    }
  },
  computed: {
    atLeastOneRequired() {
      return (
        !!this.cardIdentifier.sn.base_10 ||
        !!this.cardIdentifier.sn.base_16 ||
        !!this.cardIdentifier.sn.string ||
        'โปรดระบุข้อมูลอย่างน้อย 1 ช่อง'
      )
    },
  },
  methods: {
    toUpperCase() {
      this.cardIdentifier.sn.base_16 =
        this.cardIdentifier.sn.base_16.toUpperCase()
    },
    validateForm() {
      if (!this.isValid) {
        this.showAdvanced = true
      }
      return this.$refs.cardIdentifierForm.validate()
    },
    handleDisplayEncoding(field, item) {
      const cardIdentifier = {
        ...item,
        default_display_encoding: {
          ...item.default_display_encoding,
          [field]: !item.default_display_encoding[field],
        },
      }
      this.$emit('handleDisplayEncoding', cardIdentifier)
    },
    handleDeleteCardIdentifier(item) {
      this.$emit('handleDeleteCardIdentifier', item)
    },
  },
}
</script>
